// base class of raised errors that specifies the status code
export class RaisedError extends Error {
  statusCode: number;
  name: string;
  cause?: unknown;
  reportToSentry: boolean;

  constructor(message: string, name: string, statusCode: number, cause?: unknown) {
    super(message, { cause });
    this.name = name;
    this.statusCode = statusCode;
    this.reportToSentry = true;
  }
}

export class NotFoundError extends RaisedError {
  constructor(message: string = "Resource not found", name: string = "NotFoundError", cause?: unknown) {
    super(message, name, 404, cause);
  }
}

export class ConfigError extends RaisedError {
  constructor(message: string = "Configuration error", name: string = "ConfigError", cause?: unknown) {
    super(message, name, 500, cause);
  }
}

export class WebsiteNotFoundError extends NotFoundError {
  constructor(websiteId: string, orgId: string, message = "Website not found", cause?: unknown) {
    super(`${message}: ID ${websiteId} for org ${orgId}`, "WebsiteNotFoundError", cause);
  }
}

export class IntegrationNotFoundError extends NotFoundError {
  constructor(integrationId: string, message = "Integration not found", cause?: unknown) {
    super(`${message}: ID ${integrationId}`, "IntegrationNotFoundError", cause);
  }
}

export class LocationNotFoundError extends NotFoundError {
  constructor(websiteID: string, message = "Location not found", cause?: unknown) {
    super(`${message}: Website ID ${websiteID}`, "LocationNotFoundError", cause);
  }
}
export class IntegrationInstallationNotFoundError extends NotFoundError {
  constructor(
    message: string = "Integration installation data expired or not found, please restart installation",
    cause?: unknown
  ) {
    super(message, "IntegrationInstallationNotFoundError", cause);
  }
}

// UnauthorizedError is a special case that is not reported to Sentry
// because it is expected to be thrown when a user is not authenticated.
// children of UnauthorizedError will not report to Sentry by default.
// If you want to report an UnauthorizedError or a child of UnauthorizedError
// to Sentry, you must set reportToSentry to true.
export class UnauthorizedError extends RaisedError {
  constructor(message: string = "Unauthorized", name: string = "UnauthorizedError", cause?: unknown) {
    super(message, name, 401, cause);
    this.reportToSentry = false;
  }
}
export class NoOrgIdError extends UnauthorizedError {
  constructor(message: string = "No organization ID provided", cause?: unknown) {
    super(message, "NoOrgIdError", cause);
  }
}

// conflicts (409) are expected business-rule rejections — "already clocked in",
// "already exists", "overlaps an existing entry". like its v1 counterpart
// (ApiErrors.conflict), it does not page sentry. children inherit this; set
// reportToSentry = true on a child to override.
export class ConflictError extends RaisedError {
  constructor(message: string = "Conflict", name: string = "ConflictError", cause?: unknown) {
    super(message, name, 409, cause);
    this.reportToSentry = false;
  }
}

// forbidden (403) is an expected permission/ownership denial, not a bug. like
// its v1 counterpart (ApiErrors.forbidden), it does not page sentry. children
// inherit this; set reportToSentry = true on a child to override.
export class ForbiddenError extends RaisedError {
  constructor(message: string = "Forbidden access", name: string = "ForbiddenError", cause?: unknown) {
    super(message, name, 403, cause);
    this.reportToSentry = false;
  }
}

export class MismatchedOrgIdError extends ForbiddenError {
  constructor(message: string = "Organization ID mismatch", cause?: unknown) {
    super(message, "MismatchedOrgIdError", cause);
    this.reportToSentry = false;
  }
}

export class OAuthError extends UnauthorizedError {
  constructor(message: string = "OAuth process failed", name: string = "OAuthError", cause?: unknown) {
    super(message, name, cause);
    this.reportToSentry = true;
  }
}

export class AuthorizationCodeAlreadyUsedError extends OAuthError {
  constructor(message = "Authorization code already used. Please try authenticating again.", cause?: unknown) {
    super(message, "AuthorizationCodeAlreadyUsedError", cause);
  }
}

export class AuthorizationCodeExpiredError extends OAuthError {
  constructor(message = "Authorization code has expired. Please try authenticating again.", cause?: unknown) {
    super(message, "AuthorizationCodeExpiredError", cause);
  }
}

export class AuthorizationCodeMissingError extends OAuthError {
  constructor(message = "Authorization is missing. Please try authenticating again.", cause?: unknown) {
    super(message, "AuthorizationCodeMissingError", cause);
  }
}

// BadRequestError (400) deliberately stays reporting: it mirrors v1's
// validation_error (the malformed-request signal worth surfacing), not v1's
// bad_request. when a 400 is an expected, handled rejection (probe routes,
// known guards) throw ExpectedError instead — that is the silent-400 variant.
export class BadRequestError extends RaisedError {
  constructor(message = "Bad request", cause?: unknown) {
    super(message, "BadRequestError", 400, cause);
  }
}

export class NoFacebookPagesFoundError extends NotFoundError {
  constructor(cause?: unknown) {
    super("No Facebook pages found for user", "NoFacebookPagesFoundError", cause);
  }
}

export class InternalError extends RaisedError {
  constructor(message = "An internal error occurred", cause?: unknown) {
    super(message, "InternalError", 500, cause);
  }
}

export class ExpectedError extends RaisedError {
  constructor(message: string, cause?: unknown) {
    super(message, "ExpectedError", 400, cause);
    this.reportToSentry = false;
  }
}

export class ExternalError extends RaisedError {
  constructor(message: string, cause?: unknown) {
    super(message, "ExternalError", 503, cause);
  }
}

export class TemporaryServiceError extends ExternalError {
  constructor(message: string = "A temporary service issue occurred. Please try again in a moment.", cause?: unknown) {
    super(message, cause);
    this.name = "TemporaryServiceError";
  }
}

export class NotificationError extends ExternalError {
  constructor(message: string, cause?: unknown) {
    super(message, cause);
    this.name = "NotificationError";
  }
}

export class RainforestApiError extends ExternalError {
  operation: string;
  details?: Record<string, unknown>;

  constructor(operation: string, message: string, details?: Record<string, unknown>) {
    super(`rainforest ${operation}: ${message}`);
    this.name = "RainforestApiError";
    this.operation = operation;
    this.details = details;
  }
}

export class PrismaError extends RaisedError {
  code?: string;
  meta?: Record<string, any>;

  constructor(message: string, statusCode: number = 400, code?: string, meta?: Record<string, any>, cause?: unknown) {
    super(message, "PrismaError", statusCode, cause);
    this.code = code;
    this.meta = meta;
  }
}

/**
 * Checks if an error is a Prisma error by examining its constructor name
 */
export function isPrismaError(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === "object" &&
    "constructor" in err &&
    err.constructor !== null &&
    typeof err.constructor === "function" &&
    err.constructor.name.startsWith("PrismaClient")
  );
}

/**
 * Checks if an error is a PrismaClientKnownRequestError with a specific code.
 * Use this for handling specific Prisma error scenarios.
 *
 * @example
 * ```ts
 * try {
 *   await prisma.user.create({ data: { email } });
 * } catch (err) {
 *   if (isPrismaErrorCode(err, "P2002")) {
 *     // handle unique constraint violation
 *   }
 * }
 * ```
 */
export function isPrismaErrorCode(err: unknown, code: string): boolean {
  return (
    err !== null &&
    typeof err === "object" &&
    "constructor" in err &&
    err.constructor !== null &&
    typeof err.constructor === "function" &&
    err.constructor.name === "PrismaClientKnownRequestError" &&
    "code" in err &&
    err.code === code
  );
}

/**
 * Checks if the error is a unique constraint violation (P2002).
 * Common when inserting duplicate records.
 */
export function isPrismaUniqueConstraintError(err: unknown): boolean {
  return isPrismaErrorCode(err, "P2002");
}

/**
 * Checks if the error is a record not found error (P2025).
 * Common when updating/deleting non-existent records.
 */
export function isPrismaNotFoundError(err: unknown): boolean {
  return isPrismaErrorCode(err, "P2025");
}

/**
 * Checks if the error is a foreign key constraint failure (P2003).
 * Common when referencing non-existent related records.
 */
export function isPrismaForeignKeyError(err: unknown): boolean {
  return isPrismaErrorCode(err, "P2003");
}

/**
 * Extracts the `meta.target` field names from a Prisma error (e.g., P2002 unique constraint).
 * Returns an empty array if the error doesn't have target metadata.
 */
export function getPrismaErrorTarget(err: unknown): string[] {
  if (
    err !== null &&
    typeof err === "object" &&
    "meta" in err &&
    err.meta !== null &&
    typeof err.meta === "object" &&
    "target" in err.meta &&
    Array.isArray(err.meta.target)
  ) {
    return err.meta.target as string[];
  }
  return [];
}

// transient infrastructure blips — retryable connection/pool/timeout/write-conflict
// failures, not application bugs. on serverless (vercel + neon) the pool codes
// P2024 (timed out fetching a connection) and P2037 (too many connections) are the
// dominant transients. canonical source of truth: both isPrismaTransientError and
// formatPrismaError key off this set so the two never drift.
export const TRANSIENT_PRISMA_ERROR_CODES = [
  "P1001", // can't reach database server
  "P1002", // database server timed out
  "P1008", // operation timed out
  "P1017", // server closed the connection
  "P2024", // timed out fetching a new connection from the pool
  "P2034", // transaction write conflict / deadlock
  "P2037", // too many database connections opened
] as const;

/**
 * Checks if the error is a transient Prisma error that should trigger a retry.
 * Includes connection errors, timeouts, pool exhaustion, and write conflicts.
 */
export function isPrismaTransientError(err: unknown): boolean {
  return TRANSIENT_PRISMA_ERROR_CODES.some((code) => isPrismaErrorCode(err, code));
}

/**
 * Formats Prisma errors into user-friendly error responses
 * Returns a properly formatted error object with status code, message, and optional code
 *
 * SECURITY: This function sanitizes error messages to prevent leaking internal
 * database details (hostnames, connection strings, etc.) to the frontend.
 */
export function formatPrismaError(err: any): { statusCode: number; message: string; code?: string } {
  // Default response - NEVER include raw err.message as it may contain sensitive info
  const response = {
    statusCode: 400,
    message: "A database error occurred. Please try again later.",
    code: err.code,
  };

  // Check error type to provide more context
  if (err.constructor.name === "PrismaClientKnownRequestError") {
    // transient connection/pool/timeout/write-conflict blips → 503, retryable.
    // keyed off the canonical TRANSIENT_PRISMA_ERROR_CODES set so this never
    // drifts from isPrismaTransientError (which v1 uses to skip sentry).
    if (isPrismaTransientError(err)) {
      response.statusCode = 503;
      response.message = "The database is temporarily unavailable. Please try again in a moment.";
      return response;
    }

    // Handle known request errors with error codes
    switch (err.code) {
      case "P1003":
        response.statusCode = 500;
        response.message = "Database configuration error. Please contact support.";
        break;
      // Data errors - safe to be more specific
      case "P2002":
        const target = err.meta?.target ? `(${err.meta.target.join(", ")})` : "";
        response.message = `A record with this unique constraint already exists ${target}.`;
        break;
      case "P2003":
        response.message = "Foreign key constraint failed on the field.";
        break;
      case "P2025":
        response.message = "Record not found.";
        break;
      case "P2022":
        response.message = "There was an error fetching from the database.";
        break;
      case "P2014":
        response.message = "The change you're trying to make would violate a required relation.";
        break;
      case "P2010":
        response.message = "Raw query failed. Please contact support.";
        break;
      default:
        // SECURITY: Don't include err.message in the response - it may contain
        // sensitive information like database hostnames, connection strings, etc.
        response.statusCode = 500;
        response.message = "A database error occurred. Please try again later.";
    }
  } else if (err.constructor.name === "PrismaClientValidationError") {
    response.message = "Invalid data provided. Please check your input.";
  } else if (err.constructor.name === "PrismaClientRustPanicError") {
    response.statusCode = 500;
    response.message = "A critical database error occurred. Please try again later.";
  } else if (err.constructor.name === "PrismaClientInitializationError") {
    response.statusCode = 503;
    response.message = "Could not connect to the database. Please try again later.";
  } else if (err.constructor.name === "PrismaClientUnknownRequestError") {
    response.statusCode = 500;
    response.message = "An unexpected database error occurred. Please try again later.";
  }

  return response;
}

export class ClerkError extends RaisedError {
  clerkErrorCode?: string;

  constructor(message: string, statusCode: number = 401, clerkErrorCode?: string, cause?: unknown) {
    super(message, "ClerkError", statusCode, cause);
    this.clerkErrorCode = clerkErrorCode;
  }
}

export function isClerkError(err: any): boolean {
  // Clerk errors are identified by the clerkError flag (always true) or clerkTraceId property
  // This prevents false positives from other APIs that happen to have errors and status properties
  return err && typeof err === "object" && ("clerkError" in err || "clerkTraceId" in err);
}

/**
 * Extracts error information from a Clerk error
 * Returns a properly formatted error object with status code and message
 */
export function extractClerkErrorInfo(err: any): { statusCode: number; message: string } {
  let statusCode = err.status || 500;
  let message = "Authentication service error";

  // Handle errors with populated errors array (standard Clerk errors)
  if (err.errors && Array.isArray(err.errors) && err.errors.length > 0) {
    const clerkError = err.errors[0];
    message = clerkError.longMessage || clerkError.message || message;

    // Map Clerk error codes to appropriate HTTP status codes
    if (clerkError.code) {
      switch (clerkError.code) {
        case "resource_not_found":
          statusCode = 404;
          break;
        case "authentication_error":
        case "token_expired":
        case "token_invalid":
          statusCode = 401;
          break;
        case "forbidden":
          statusCode = 403;
          break;
        case "rate_limit_exceeded":
          statusCode = 429;
          break;
        case "validation_error":
          statusCode = 400;
          break;
        default:
          statusCode = err.status || 500;
      }
    }
  }
  // Handle gateway/infrastructure errors (502, 503, 504) with empty errors array
  else if (err.status) {
    statusCode = err.status;

    // Provide user-friendly messages for gateway errors
    switch (err.status) {
      case 502:
        message = "The authentication service is temporarily unavailable. Please try again in a moment.";
        break;
      case 503:
        message = "The authentication service is currently undergoing maintenance. Please try again shortly.";
        break;
      case 504:
        message = "The authentication service timed out. Please try again.";
        break;
      default:
        message = err.message || message;
    }
  }

  return { statusCode, message };
}
